import { EventEmitter, Injectable } from '@angular/core';
import { Observable, combineLatest, from } from 'rxjs';
import { throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ICreditCardSummaryData } from '../common/models/credit-card.model';
import { HttpRequest, HttpProviderService, IHttpSuccessPayload } from '@fpx/core';
import { AccountDetails, ILoanAccountDetail, ILoanProduct, ILoans, IMutualFundsAccountDetail, IMutualFundsProduct, ITermDepositDetails, ITermDepositProduct } from '../models/account-info.model';
import { CustomerInfo, IAccountBalance, INickname } from '../models/customer-info.model';
import { DebitCardDetail } from '../models/Debit-card-details.model';
import { DepositDetail } from '../models/Deposit-details.model';
import { LoanDetails } from '../models/loan-details.model';
import { UserProfile } from '../models/userProfile.model';
import { ProvidehttpService } from '../providehttp.service';
import { environment } from 'src/environments/environment';
import { CommonService } from '../common.service';
import { UserService } from '../user.service';
import { NativeStorageService } from './mobile/native-storage.service';

@Injectable({
  providedIn: 'root'
})
export class CustomerService {

  customerInfo: CustomerInfo;
  accountsNickname: any;
  nicknames: any = [];
  userProfilePic: string;
  currentUserName: any;

  isPrimaryUser: boolean;
  cards: any;
  refreshAccountsEmit: EventEmitter<any>;

  constructor(
    private _nativeStorageService: NativeStorageService,
    private httpService: ProvidehttpService,
    private _httpProvider: HttpProviderService,
    private _commonService: CommonService,
    private _userService: UserService) { }

  /**
   * @description to get customer name, address, email, contact, ...
   * @returns UserProfile
   */
  getUserProfile(): UserProfile {
    let userProfile: UserProfile;
    if (this.customerInfo) {
      userProfile = {
        customerAddress: this.customerInfo.address,
        customerEmailId: this.customerInfo.emailId,
        customerCode: this.customerInfo.customerCode,
        customerFirstName: this.customerInfo.firstName,
        customerLastName: this.customerInfo.lastName,
        customerMobileNumber: this.customerInfo.mobileNumber,
        customerMiddleName: this.customerInfo.middleName
      }
    }
    return userProfile;
  }

  getCustomerFullName(): string {
    return this.customerInfo.firstName + " " + this.customerInfo.middleName + " " + this.customerInfo.lastName;
  }

getAccount(accType: 'CA' | 'SA' | 'ALL' = 'ALL'): AccountDetails[] {
  let result = null;

  if (accType == "CA") {
    result = this.customerInfo.accountInfo.currentAccounts;
  } else if (accType == "SA") {
    result = this.customerInfo.accountInfo.savingsAccounts;
  } else {
    result = [].concat(
      this.customerInfo.accountInfo.currentAccounts || [],
      this.customerInfo.accountInfo.savingsAccounts || [],
      this.customerInfo.accountInfo.accountDetails || []
    );
  }

  if (!result) {
    return [];
  }

  if (!Array.isArray(result)) {
    const values = Object.values(result);
    if (
      values.length === 0 ||
      (values.length === 1 && values[0] == null)
    ) {
      return [];
    }
    return [result];
  }

  return result;
}

  getLoans(): ILoans {
    return this.customerInfo.accountInfo.loans;
  }

  getLoansProduct(): ILoanProduct[] {
    return this.customerInfo.accountInfo.loans.product;
  }

  getdepositsProduct(): ITermDepositProduct[] {
    return this.customerInfo.accountInfo.termDeposits.product;
  }


  getLoanDetails(): LoanDetails[] {
    return this.customerInfo.accountInfo.loanDetails;
  }

  /* Deposit related functions start */
  getDeposits(): ITermDepositDetails {
    return this.customerInfo.accountInfo.termDeposits;
  }
  getDepositOverview(): ITermDepositDetails {
    //return this.customerInfo.accountInfo.depositOverview;
    return this.customerInfo.accountInfo.termDeposits;
  }
  getDepositSummary(): ITermDepositProduct[] {
    return this.customerInfo.accountInfo.termDeposits.product;
  }

  getDepositDetails(): DepositDetail[] {
    return this.customerInfo.accountInfo.depositDetails;
  }

  getTotalDepositAmount() {
    return this.customerInfo.accountInfo.depositOverview.tdAmount;
  }
  /* Deposit related functions end */

  getMutualFundsOverview() {
    return this.customerInfo.accountInfo.mutualFunds;
  }
  getMutualFundsSummary() {
    return this.customerInfo.accountInfo.mutualFunds.product;
  }

  /* Debit card related functions start */

  getDebitCardDetails(): DebitCardDetail[] {
    return this.customerInfo.accountInfo.debitCardDetails;
  }

  setDebitCardDetails(res) {
    this.customerInfo.accountInfo.debitCardDetails = res;
  }
  /* Debit related functions end */

  getCreditCardSummary(): ICreditCardSummaryData[] {
    return this.customerInfo.accountInfo.creditCardDetails;
  }

  getAccountBalance(): IAccountBalance {
    return {
      totalAsset: this.customerInfo.totalAsset,
      totalLiability: this.customerInfo.totalLiability
    }
  }

  getCreditCardDetails(): ICreditCardSummaryData[] {
    return this.customerInfo.accountInfo.creditCardDetails;
  }

  /**
   *
   * @param userId
   * @returns CustomerInfo
   * @description To fetch customer information based on param userId, which contains profile details and account details.
   */
  fetchCustomerInfo(userId: string): Observable<any> {

    const customerInfo$ = this.fetchAccountsInfo();
    const nicknamesInfo$ = this.fetchNicknamesInfo();

    return combineLatest([customerInfo$, nicknamesInfo$]).pipe(
      map((result) => {
        let customerInfo = result[0];
        this.accountsNickname = result[1];

        if (this.accountsNickname) {
          this.accountsNickname.forEach(account => {
            this.updateNickName(account.accountNumber, account.nickName);
          });
        }

        this.customerInfo = customerInfo;

        return this.customerInfo;
      })
    );
  }

  fetchAccountsInfo(): Observable<CustomerInfo> {
    const httpRequest = new HttpRequest();
    httpRequest.setResource('/customer/{customerCode}');
    httpRequest.setMethod('GET');
    httpRequest.addPathParameter('customerCode', "U0000001");
    // httpRequest.addPathParameter('customerCode', this._userService.getCustomerCode());

    httpRequest.setServerContext(environment.customersPublisher);
    return this._httpProvider.invokeRestApi(httpRequest).pipe(
      map(
        (res: IHttpSuccessPayload<any>) => {
          console.log("3: get customer data");
          // this.customerInfo = res.body['customer'];
          this._commonService.baseCurrency = res?.body?.['customer'].baseCurrency;
          this._commonService.serverDate = res?.body?.['customer']?.serverDate;
          // this._commonService.serverDate = '2023-12-09';
          return res?.body['customer'];
        }
      )
    );
  }

  fetchNicknamesInfo(): Observable<INickname[]> {
    const httpRequest = new HttpRequest();
    httpRequest.setResource('/accountnickname');
    httpRequest.setMethod('GET');
    httpRequest.setServerContext(environment.accountsPublisher);
    return this._httpProvider.invokeRestApi(httpRequest).pipe(
      map((res: IHttpSuccessPayload<any>) => {
        let accountsNickname = res?.body?.accountnickname || null;
        this.accountsNickname = accountsNickname;
        return this.accountsNickname;
      })
    );
  }

  getLoanSummaryDetails() {
    return this.customerInfo.accountInfo.loans;
  }
  getLoanAccounts(): ILoanAccountDetail[] {
    let products = this.customerInfo.accountInfo?.loans?.product;

    let list = [];
    products.forEach(product => {
      list = list.concat(product.accountDetails);
    });

    return list;
  }

  updateNickName(accountNumber: string, nickName: string) {
    let info = this.customerInfo?.accountInfo;
    let accounts = info?.accountDetails;
    let loans = [];
    info?.loans?.product?.forEach(loan => {
      loans = loans.concat(loan.accountDetails);
    });
    let deposits = [];
    info?.termDeposits?.product?.forEach(deposit => {
      deposits = deposits?.concat(deposit.accountDetails);
    });
    let _acc = accounts?.find(obj => obj?.accountNumber == accountNumber) || loans?.find(obj => obj?.accountNumber == accountNumber) || deposits.find(obj => obj?.accountNumber == accountNumber);
    if (_acc) {
      _acc.nickName = nickName;
    }
  }

  getAccountsSummary() {
    let accounts = [];
    // let data = this.accountsService.getList();

    if (this.customerInfo.accountInfo.accountDetails) {
      let acc = this.customerInfo.accountInfo.accountDetails.map((item) => {
        return {
          ...item,
          category: "account", isAccountCard: true, isAccount: true
        }
      });

      accounts.push({
        category: "account",
        label: 'OVERVIEWUI.PORTFOLIOSUMMARYLIST.Accounts',
        summary: acc,
        total: this.customerInfo.accountInfo.totalCASAAmount,
        currency: acc[0]?.accountCurrency,

      });

    } else {
      if (this.getAccount('CA') && this.getAccount('CA').length) {
        if (this.getAccount('CA')) {
          let acc = this.getAccount('CA').map((item) => {
            return { ...item, category: "CA", isAccountCard: true, isAccount: true }
          });
          accounts.push({
            category: "account",
            label: 'OVERVIEWUI.PORTFOLIOSUMMARYLIST.currentAccount',
            summary: acc,
            total: this.customerInfo.accountInfo.totalCAAmount,
            currency: acc[0]?.accountCurrency,
            accountType: 'CA'
          });
        }
      } else {
        accounts.push({
          category: "account",
          label: 'OVERVIEWUI.PORTFOLIOSUMMARYLIST.currentAccount',
          summary: [],
          accountType: 'CA'
        });
      }

      if (this.getAccount('SA') && this.getAccount('SA').length) {
        if (this.getAccount('SA')) {
          let acc = this.getAccount('SA').map((item) => {
            return {
              ...item, category: "SA",
              // nickName: this.accountsService.getNickname(item.accountNumber),
              isAccountCard: true, isAccount: true,
            }
          });

          accounts.push({
            category: "account",
            label: 'OVERVIEWUI.PORTFOLIOSUMMARYLIST.savingAccounts',
            summary: acc,
            total: this.customerInfo.accountInfo.totalSAAmount,
            currency: acc[0]?.accountCurrency,
            accountType: 'SA'
          });

        }
      } else {
        accounts.push({
          category: "account",
          label: 'OVERVIEWUI.PORTFOLIOSUMMARYLIST.savingAccounts',
          summary: [],
          accountType: 'SA'
        });
      }
    }
    if (this.customerInfo.accountInfo.termDeposits) {
      let acc = this.getDepositSummary();
      accounts.push({
        category: "deposit",
        label: 'OVERVIEWUI.PORTFOLIOSUMMARYLIST.deposits',
        summary: acc,
        total: acc?.[0]?.totalDepositAmount,
        currency: this._commonService.baseCurrency,
        accountType: 'deposit'
      });
    } else {
      accounts.push({
        category: "deposit",
        label: 'OVERVIEWUI.PORTFOLIOSUMMARYLIST.deposits',
        summary: [],
        accountType: 'deposit'
      });
    }


    if (this.customerInfo.accountInfo.loans) {
      let loanSummary = this.getLoanAccounts();
      accounts.push({
        category: "loan",
        label: "Loans",
        summary: loanSummary,
        accountType: 'loan',
        total: this.customerInfo.accountInfo.loans.totalOutstandingAmount,
        currency: this._commonService.baseCurrency
        //customerName:this.customerInfo.firstName+" "+this.customerInfo.middleName+" "+this.customerInfo.lastName
      });
    }



    if (this.customerInfo.accountInfo.mutualFunds) {
      let acc = this.getMutualFundsSummary();
      accounts.push({
        category: "mutualFund",
        label: 'OVERVIEWUI.PORTFOLIOSUMMARYLIST.mutualFunds',
        summary: acc,
        total: this.customerInfo.accountInfo.mutualFunds.totalUnits || 0,
        currency: this._commonService.baseCurrency,
        accountType: 'mutualFund'
      });
    }

    if (this.customerInfo.accountInfo.creditCardDetails) {
      let _data = this.customerInfo.accountInfo.creditCardDetails;
      const totalOutstanding = _data.reduce((ttlOutstanting, item) => ttlOutstanting + Number(item.outstandingAmount), 0);
      let creditCardsList = this.customerInfo.accountInfo.creditCardDetails.map((item) => {
        return {
          ...item,
          'category': 'credit-card'
        }
      });

      accounts.push({
        category: "creditCard",
        label: 'OVERVIEWUI.PORTFOLIOSUMMARYLIST.creditCard',
        summary: creditCardsList,
        total: totalOutstanding || 0,
        currency: this.customerInfo.accountInfo.creditCardDetails[0].currency,
        accountType: 'creditCard'
      });
    }

    let summaryData: any;
    return summaryData = {
      accounts: accounts
    }
  }

  async fetchAccountsNickname() {
    const httpRequest = new HttpRequest();
    httpRequest.setResource('/accountnickname');
    httpRequest.setMethod('GET');
    httpRequest.setServerContext(environment.accountsPublisher);
    httpRequest.addPathParameter('userId', this.customerInfo.customerCode);
    await this._httpProvider.invokeRestApi(httpRequest).subscribe({
      next: (res) => {
        this.nicknames = res.body.accountnickname;
      }
    });
  }

  getNickname(accountNumber: string): string {
    let nickName: string = this.nicknames?.find(x => {
      return x?.accountNumber == accountNumber;
    })?.nickName;
    return nickName;
  }

  getNicknameInventoryNumber(accountNumber: string): string {
    let inventoryNumber: string = this.nicknames?.find(x => {
      return x?.accountNumber == accountNumber;
    })?.inventoryNumber;
    return inventoryNumber;
  }

  // refreshNickname(data:any){
  //   let nicknameData = this.nicknames?.find(x=>{
  //     return x?.accountNumber == data.accountNumber;
  //   });
  //   if(!nicknameData){
  //     this.nicknames.push(data);
  //   } else{
  //     nicknameData.nickName = data.nickName;
  //   }

  // }
  refreshNickname(data: any, action?) {
    let nicknameData = this.nicknames?.find(x => {
      return x?.accountNumber == data.accountNumber;
    });

    if ((nicknameData) && action == 'DELETE') {
      //TODO: delete the recored 'nicknameData' from the array this.nicknames
      this.nicknames = this.nicknames?.filter(x => nicknameData.accountNumber != x.accountNumber);

    } else if ((nicknameData) && action == 'MODIFY') {
      let objIndex;
      objIndex = this.nicknames.findIndex((obj => obj.accountNumber == data.accountNumber));
      this.nicknames[objIndex].nickName = data.nickName
    } else {
      this.nicknames.push(data);
    }
  }

  setUserProfilePic(imageData: string) {
    this.userProfilePic = imageData;
  }

  getUserProfilePic(): string {
    return this.userProfilePic;
  }

  fetchUserProfilePic() {
    const httpRequest = new HttpRequest();
    httpRequest.setResource('/userprofile/{userId}');
    httpRequest.setMethod('GET');
    httpRequest.setServerContext(environment.customersPublisher);
    httpRequest.addPathParameter('userId', this._userService.customerCode);
    return this._httpProvider.invokeRestApi(httpRequest).pipe(
      map((res) => {
        this.setUserProfilePic(res?.body?.userprofile.photo);
        this._userService.setLastLoginDate(res?.body?.userprofile.lastLoginDateTime);
        this._userService.setLastLoginFailureDateTime(res?.body?.userprofile?.lastFailedLogin)
        return res?.body?.userprofile.photo;
      })
    );
  }





  checkPrimaryUser(): Promise<any> {
    return this._nativeStorageService.retriveData('primaryUser').then((response) => {
      if (this.currentUserName.toLowerCase() == response.toLowerCase()) {
        this.isPrimaryUser = true;
      }
      else {
        this.isPrimaryUser = false;
      }
      return this.isPrimaryUser;
    }).catch(err => {
      this.isPrimaryUser = false;
      return throwError(err);
    });


  }

   getAllCards() {
    return this.cards;
  }
  
}
